import { BODY_FONT_STACK, CSS_VAR_NAMES } from "@/components/design-tokens";
import { normalizeLegacyWidgetFontStack } from "@qa-app/core/widget-fonts";
import { z } from "zod";

/**
 * System font stack that works beautifully across all platforms:
 * - macOS/iOS: San Francisco (-apple-system)
 * - Windows: Segoe UI
 * - Android/Chrome OS: Roboto
 * - Linux: Noto Sans / Liberation Sans
 * - Fallbacks: Helvetica Neue, Arial, sans-serif
 * - Emoji support for all platforms
 */
const SYSTEM_FONT_STACK = BODY_FONT_STACK;

/**
 * Inject base styles (including system fonts) into the shadow DOM
 * This ensures consistent font rendering across all platforms
 */
export const injectBaseStyles = (element: HTMLElement): void => {
  const shadowRoot = element.shadowRoot;
  if (!shadowRoot) {
    return;
  }

  // Check if styles already injected
  if (shadowRoot.querySelector("style[data-pollen-base-styles]")) {
    return;
  }

  const styleElement = document.createElement("style");
  styleElement.dataset.pollenBaseStyles = "true";
  styleElement.textContent = `
    :host {
      font-family: var(${CSS_VAR_NAMES.fontFamily}, ${SYSTEM_FONT_STACK});
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      text-rendering: optimizeLegibility;
    }
    
    *, *::before, *::after {
      box-sizing: border-box;
    }
  `;
  shadowRoot.prepend(styleElement);
};

export type WidgetAttributes = {
  apiKey?: string;
  apiUrl?: string;
  suggestedQuestions?: string[];
  placeholderText?: string;
  // Legacy color attributes (still supported for backwards compatibility)
  textColor?: string;
  backgroundColor?: string;
  // New color attributes
  primaryColor?: string;
  cardBackgroundColor?: string;
  textSecondaryColor?: string;
  borderColor?: string;
  accentColor?: string;
  fontFamily?: string;
  headingFontFamily?: string;
};

const parseSuggestedQuestions = (element: HTMLElement): string[] | undefined => {
  const questionsAttribute = element.getAttribute("questions");
  if (!questionsAttribute) {
    return undefined;
  }

  const questions = questionsAttribute
    .split(";")
    .map(question => question.trim())
    .filter(question => question.length > 0)
    .slice(0, 3);

  return questions.length > 0 ? questions : undefined;
};

const readFontFamilyAttribute = (element: HTMLElement, name: string): string | undefined => {
  const value = element.getAttribute(name)?.trim();
  return value ? normalizeLegacyWidgetFontStack(value) : undefined;
};

/**
 * Widget theme configuration
 * Supports all 6 configurable colors for full light/dark theme support
 */
export type WidgetTheme = {
  textColor?: string;
  backgroundColor?: string;
  primaryColor?: string;
  cardBackgroundColor?: string;
  textSecondaryColor?: string;
  borderColor?: string;
  accentColor?: string;
  fontFamily?: string;
  headingFontFamily?: string;
};

export type WidgetThemeFetchError = {
  kind: "unauthorized" | "forbidden" | "http_error" | "network_error" | "invalid_response";
  status?: number;
  message: string;
};

export type WidgetThemeFetchResult = {
  theme: WidgetTheme | null;
  showChatQuestions: boolean;
  disclaimerText?: string;
  suggestedQuestions?: string[];
  placeholderText?: string;
  organizationSlug?: string;
  langfuse?: {
    publicKey: string;
    baseUrl: string;
    environment?: string;
  } | null;
  error?: WidgetThemeFetchError;
};

// Keep track of values this runtime assigned so an API refresh can update its
// own inline values without clobbering a value supplied by the embedding host.
const widgetAssignedThemeVariables = new WeakMap<HTMLElement, Map<string, string>>();

const hasHostProvidedThemeVariable = (element: HTMLElement, variableName: string): boolean => {
  const inlineValue = element.style.getPropertyValue(variableName).trim();
  const assignedVariables = widgetAssignedThemeVariables.get(element);
  if (inlineValue) {
    if (assignedVariables?.get(variableName) === inlineValue) {
      return false;
    }
    assignedVariables?.delete(variableName);
    return true;
  }

  const computedValue = getComputedStyle(element).getPropertyValue(variableName).trim();
  if (computedValue) {
    assignedVariables?.delete(variableName);
    return true;
  }

  return false;
};

const setThemeVariable = (
  element: HTMLElement,
  variableName: string,
  value: string,
  overrideHostValue: boolean,
): void => {
  if (!overrideHostValue && hasHostProvidedThemeVariable(element, variableName)) {
    return;
  }

  element.style.setProperty(variableName, value);
  const assignedVariables = widgetAssignedThemeVariables.get(element) ?? new Map<string, string>();
  assignedVariables.set(variableName, element.style.getPropertyValue(variableName).trim());
  widgetAssignedThemeVariables.set(element, assignedVariables);
};

const normalizeHexColor = (value: string | undefined): string | undefined => {
  if (!value) {
    return undefined;
  }
  return value.startsWith("#") ? value : `#${value}`;
};

// Centralised so each web component picks up new attribute fields without
// having to remember to copy them into its own literal.
export const buildAttributeOverrides = (attributes: WidgetAttributes): WidgetTheme => ({
  textColor: attributes.textColor,
  backgroundColor: attributes.backgroundColor,
  primaryColor: attributes.primaryColor,
  cardBackgroundColor: attributes.cardBackgroundColor,
  textSecondaryColor: attributes.textSecondaryColor,
  borderColor: attributes.borderColor,
  accentColor: attributes.accentColor,
  fontFamily: attributes.fontFamily,
  headingFontFamily: attributes.headingFontFamily,
});

export const readWidgetAttributes = (element: HTMLElement): WidgetAttributes => {
  return {
    apiKey: element.getAttribute("api-key")?.trim() ?? undefined,
    apiUrl: element.getAttribute("api-url")?.trim() ?? undefined,
    suggestedQuestions: parseSuggestedQuestions(element),
    placeholderText: element.getAttribute("placeholder")?.trim() || undefined,
    // Core colors
    textColor: normalizeHexColor(element.getAttribute("text-color") ?? undefined),
    backgroundColor: normalizeHexColor(element.getAttribute("background-color") ?? undefined),
    // Extended colors
    primaryColor: normalizeHexColor(element.getAttribute("primary-color") ?? undefined),
    cardBackgroundColor: normalizeHexColor(element.getAttribute("card-background-color") ?? undefined),
    textSecondaryColor: normalizeHexColor(element.getAttribute("text-secondary-color") ?? undefined),
    borderColor: normalizeHexColor(element.getAttribute("border-color") ?? undefined),
    accentColor: normalizeHexColor(element.getAttribute("accent-color") ?? undefined),
    fontFamily: readFontFamilyAttribute(element, "font-family"),
    headingFontFamily: readFontFamilyAttribute(element, "heading-font-family"),
  };
};

/**
 * Apply theme CSS variables to an element
 * Sets all 6 color variables for full theme support
 */
export const applyThemeVariables = (
  element: HTMLElement,
  theme: WidgetTheme,
  overrideHostValues = false,
) => {
  // Apply each color if provided, using the centralized CSS variable names
  if (theme.textColor) {
    setThemeVariable(element, CSS_VAR_NAMES.textColor, theme.textColor, overrideHostValues);
  }
  if (theme.backgroundColor) {
    setThemeVariable(element, CSS_VAR_NAMES.backgroundColor, theme.backgroundColor, overrideHostValues);
  }
  if (theme.primaryColor) {
    setThemeVariable(element, CSS_VAR_NAMES.primaryColor, theme.primaryColor, overrideHostValues);
  }
  if (theme.cardBackgroundColor) {
    setThemeVariable(element, CSS_VAR_NAMES.cardBackgroundColor, theme.cardBackgroundColor, overrideHostValues);
  }
  if (theme.textSecondaryColor) {
    setThemeVariable(element, CSS_VAR_NAMES.textSecondaryColor, theme.textSecondaryColor, overrideHostValues);
  }
  if (theme.borderColor) {
    setThemeVariable(element, CSS_VAR_NAMES.borderColor, theme.borderColor, overrideHostValues);
  }
  if (theme.accentColor) {
    setThemeVariable(element, CSS_VAR_NAMES.accentColor, theme.accentColor, overrideHostValues);
  }
  if (theme.fontFamily) {
    setThemeVariable(element, CSS_VAR_NAMES.fontFamily, theme.fontFamily, overrideHostValues);
  }
  if (theme.headingFontFamily) {
    setThemeVariable(element, CSS_VAR_NAMES.headingFontFamily, theme.headingFontFamily, overrideHostValues);
  }
};

/**
 * API response type for widget config
 */
type WidgetConfigApiResponse = {
  organizationSlug?: string;
  widget?: {
    textColor?: string;
    backgroundColor?: string;
    primaryColor?: string;
    cardBackgroundColor?: string;
    textSecondaryColor?: string;
    borderColor?: string;
    accentColor?: string;
    fontFamily?: string;
    headingFontFamily?: string;
    bodyFont?: { fontFamily: string; fontId?: string };
    headingFont?: { fontFamily: string; fontId?: string };
    showChatQuestions?: boolean;
    disclaimerText?: string;
    suggestedQuestions?: string[];
    placeholderText?: string;
  };
  langfuse?: {
    publicKey?: string;
    baseUrl?: string;
    environment?: string;
  } | null;
} | null;

const resolvedRuntimeFontSchema = z.object({
  fontFamily: z.string().trim().min(1),
  fontId: z.string().trim().min(1).max(100).optional(),
});

const widgetConfigApiResponseSchema = z.object({
  organizationSlug: z.string().optional(),
  widget: z.object({
    textColor: z.string().optional(),
    backgroundColor: z.string().optional(),
    primaryColor: z.string().optional(),
    cardBackgroundColor: z.string().optional(),
    textSecondaryColor: z.string().optional(),
    borderColor: z.string().optional(),
    accentColor: z.string().optional(),
    fontFamily: z.string().optional(),
    headingFontFamily: z.string().optional(),
    bodyFont: z.unknown().optional(),
    headingFont: z.unknown().optional(),
    // Optional content is parsed defensively below so one malformed field does
    // not discard otherwise valid theme configuration.
    showChatQuestions: z.unknown().optional(),
    disclaimerText: z.unknown().optional(),
    suggestedQuestions: z.unknown().optional(),
    placeholderText: z.unknown().optional(),
  }).nullable().optional(),
  langfuse: z.object({
    publicKey: z.string().optional(),
    baseUrl: z.string().optional(),
    environment: z.string().optional(),
  }).nullable().optional(),
}).passthrough();

// Module-level cache of in-flight + completed `/v2/widget-config` fetches.
// Multiple `pollen-*` elements on the same host page share one request keyed
// by `${apiUrl}|${apiKey}`; without this, four sibling elements
// (`pollen-chat`, `pollen-chat-trigger`, `pollen-chat-modal`,
// `pollen-search-results`) would issue identical concurrent requests per
// page load, and rate-limited tenants could see some bootstrap silently.
// AbortSignal cannot be propagated into the shared fetch — see the wrapper
// below for per-caller abort handling.
const widgetThemeCache = new Map<string, Promise<WidgetThemeFetchResult>>();

const buildCacheKey = (apiUrl: string, apiKey: string): string => `${apiUrl}|${apiKey}`;

export const __resetWidgetThemeCacheForTests = (): void => {
  widgetThemeCache.clear();
};

const fetchWidgetThemeUncached = async (apiUrl: string, apiKey: string): Promise<WidgetThemeFetchResult> => {
  try {
    const response = await fetch(`${apiUrl}/v2/widget-config`, {
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
    });

    if (!response.ok) {
      let kind: WidgetThemeFetchError["kind"] = "http_error";
      if (response.status === 401) {
        kind = "unauthorized";
      }
      else if (response.status === 403) {
        kind = "forbidden";
      }
      const error: WidgetThemeFetchError = {
        kind,
        status: response.status,
        message: `Theme request failed with status ${response.status}.`,
      };
      console.error("Tenant UI Widget: theme fetch failed.", {
        apiUrl,
        status: response.status,
      });
      return { theme: null, showChatQuestions: true, error };
    }

    const parsed = widgetConfigApiResponseSchema.safeParse(await response.json());

    if (!parsed.success) {
      const error: WidgetThemeFetchError = {
        kind: "invalid_response",
        message: "Theme response was not valid JSON.",
      };
      console.error("Tenant UI Widget: theme fetch failed.", {
        apiUrl,
        reason: "invalid_response",
      });
      return { theme: null, showChatQuestions: true, error };
    }

    const data = parsed.data as Exclude<WidgetConfigApiResponse, null>;
    const bodyFontResult = resolvedRuntimeFontSchema.safeParse(data.widget?.bodyFont);
    const headingFontResult = resolvedRuntimeFontSchema.safeParse(data.widget?.headingFont);
    const bodyFont = bodyFontResult.success ? bodyFontResult.data : undefined;
    const headingFont = headingFontResult.success ? headingFontResult.data : undefined;

    // Parse all 6 color fields from the API response
    return {
      theme: {
        textColor: normalizeHexColor(data.widget?.textColor),
        backgroundColor: normalizeHexColor(data.widget?.backgroundColor),
        primaryColor: normalizeHexColor(data.widget?.primaryColor),
        cardBackgroundColor: normalizeHexColor(data.widget?.cardBackgroundColor),
        textSecondaryColor: normalizeHexColor(data.widget?.textSecondaryColor),
        borderColor: normalizeHexColor(data.widget?.borderColor),
        accentColor: normalizeHexColor(data.widget?.accentColor),
        fontFamily: bodyFont?.fontFamily ?? data.widget?.fontFamily,
        headingFontFamily: headingFont?.fontFamily ?? data.widget?.headingFontFamily,
      },
      showChatQuestions:
        typeof data.widget?.showChatQuestions === "boolean"
          ? data.widget.showChatQuestions
          : true,
      disclaimerText: typeof data.widget?.disclaimerText === "string" ? data.widget.disclaimerText : undefined,
      suggestedQuestions: Array.isArray(data.widget?.suggestedQuestions)
        ? data.widget.suggestedQuestions
            .filter(question => typeof question === "string")
            .map(question => question.trim())
            .filter(question => question.length > 0)
            .slice(0, 3)
        : undefined,
      placeholderText: typeof data.widget?.placeholderText === "string"
        ? data.widget.placeholderText
        : undefined,
      organizationSlug:
        typeof data.organizationSlug === "string" && data.organizationSlug.length > 0
          ? data.organizationSlug
          : undefined,
      langfuse:
        data.langfuse && typeof data.langfuse.publicKey === "string" && typeof data.langfuse.baseUrl === "string"
          ? {
              publicKey: data.langfuse.publicKey,
              baseUrl: data.langfuse.baseUrl,
              ...(typeof data.langfuse.environment === "string" && data.langfuse.environment.length > 0
                ? { environment: data.langfuse.environment }
                : {}),
            }
          : null,
    };
  }
  catch (error) {
    const result: WidgetThemeFetchError = {
      kind: "network_error",
      message: error instanceof Error ? error.message : "Network error.",
    };
    console.error("Tenant UI Widget: theme fetch failed.", {
      apiUrl,
      reason: "network_error",
    });
    return { theme: null, showChatQuestions: true, error: result };
  }
};

/**
 * Cached entry point: every caller with the same `(apiUrl, apiKey)` pair
 * shares one in-flight `/v2/widget-config` fetch. Per-caller `AbortSignal`s
 * are honoured locally via `Promise.race` without interrupting the shared
 * request, so an element disconnecting mid-bootstrap doesn't drop the others.
 */
export const fetchWidgetTheme = (
  apiUrl: string,
  apiKey: string,
  signal?: AbortSignal,
): Promise<WidgetThemeFetchResult> => {
  const cacheKey = buildCacheKey(apiUrl, apiKey);
  let pending = widgetThemeCache.get(cacheKey);
  if (!pending) {
    pending = fetchWidgetThemeUncached(apiUrl, apiKey);
    widgetThemeCache.set(cacheKey, pending);
    void pending.then((result) => {
      if (result.error && widgetThemeCache.get(cacheKey) === pending) {
        widgetThemeCache.delete(cacheKey);
      }
    });
  }

  if (!signal) {
    return pending;
  }

  if (signal.aborted) {
    return Promise.resolve({ theme: null, showChatQuestions: true });
  }

  return new Promise<WidgetThemeFetchResult>((resolve) => {
    const onAbort = () => resolve({ theme: null, showChatQuestions: true });
    signal.addEventListener("abort", onAbort, { once: true });
    pending.then((result) => {
      signal.removeEventListener("abort", onAbort);
      if (!signal.aborted)
        resolve(result);
    });
  });
};

/**
 * Apply theme with attribute overrides
 * Attribute overrides take precedence over API-fetched theme
 *
 * Priority order:
 * 1. HTML attribute overrides (highest)
 * 2. API-fetched theme
 * 3. CSS variable defaults in design-tokens.ts (fallback)
 */
export const applyThemeWithOverrides = (element: HTMLElement, theme: WidgetTheme | null, overrides: WidgetTheme) => {
  if (theme) {
    applyThemeVariables(element, theme);
    applyThemeVariables(element, overrides, true);
    return;
  }

  applyThemeVariables(element, overrides, true);
};
